import { Playlists } from "./api";
import { decodeMaybeUtf8 } from "./decode";

/**
 * Shared "what's on right now" lookup for a live channel.
 *
 * One cache for the whole tab, deliberately: the Hub's Live-now rail and the
 * Live TV channel list ask about the same channels, and without a shared cache
 * a user moving between them would double the upstream requests. Misses are
 * cached too — a channel whose panel carries no guide would otherwise be
 * re-requested every time it scrolled back into view.
 *
 * Backed by `/epg/now/`, which returns ~550 bytes. Neither of the older
 * endpoints works here: `/epg/short/` omits the currently-airing programme
 * entirely, and `/epg/full/` is ~127 KB per channel.
 */

export type NowPlaying = { title: string; pct: number; endsInS: number } | null;

const cache = new Map<string, NowPlaying | Promise<NowPlaying>>();

export function loadNowPlaying(pid: number, streamId: number): Promise<NowPlaying> {
  const key = `${pid}:${streamId}`;
  const hit = cache.get(key);
  if (hit !== undefined) return Promise.resolve(hit);

  const p = Playlists.nowPlaying(pid, streamId)
    .then((d) => {
      const val: NowPlaying = d?.now
        ? { title: decodeMaybeUtf8(d.now.title), pct: d.pct || 0, endsInS: d.ends_in_s || 0 }
        : null;
      cache.set(key, val);
      return val;
    })
    .catch(() => {
      cache.set(key, null);
      return null;
    });

  cache.set(key, p);
  return p;
}
